import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { dashboardPathForRole } from "@/lib/roles";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Lock, Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

const REQUIREMENTS = [
    { test: (s) => s.length >= 8, label: "At least 8 characters" },
    { test: (s) => /[A-Z]/.test(s), label: "One uppercase letter" },
    { test: (s) => /[a-z]/.test(s), label: "One lowercase letter" },
    { test: (s) => /\d/.test(s), label: "One number" },
];

export default function ChangePassword() {
    const { user, changePassword } = useAuth();
    const navigate = useNavigate();
    const [oldPwd, setOldPwd] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [showOld, setShowOld] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const isForced = !!user?.must_change_password;
    const reqState = REQUIREMENTS.map((r) => ({ ...r, ok: r.test(newPwd) }));
    const allReqOk = reqState.every((r) => r.ok);
    const matches = newPwd && newPwd === confirmPwd;
    const canSubmit = oldPwd && allReqOk && matches && !saving;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        setError("");
        try {
            await changePassword(oldPwd, newPwd);
            toast.success("Password updated successfully");
            navigate(dashboardPathForRole(user?.role));
        } catch (err) {
            const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Could not update password.";
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="min-h-[80vh] flex items-center justify-center px-4 py-12"
            data-testid="change-password-page"
        >
            <Card className="w-full max-w-lg border-border shadow-lg shadow-primary/5">
                <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center ring-1 ring-primary/15">
                            <ShieldCheck className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl text-foreground tracking-tight leading-none">
                                {isForced ? "Set your new password" : "Change password"}
                            </h1>
                            <p className="text-xs text-muted-foreground mt-1">
                                Signed in as {user?.email}
                            </p>
                        </div>
                    </div>

                    {isForced && (
                        <div
                            className="mt-4 rounded-md border border-accent/40 bg-accent/10 px-3 py-2.5 text-sm text-foreground/85 flex items-start gap-2"
                            data-testid="forced-change-banner"
                        >
                            <AlertCircle className="h-4 w-4 text-accent-foreground/70 mt-0.5 flex-shrink-0" />
                            <span>
                                Your account was created by an administrator. Please pick a new
                                password before continuing.
                            </span>
                        </div>
                    )}

                    <form onSubmit={submit} className="space-y-4 mt-6">
                        <div>
                            <Label htmlFor="old-pwd" className="text-sm font-semibold">
                                {isForced ? "Temporary password" : "Current password"}
                            </Label>
                            <div className="relative">
                                <Input
                                    id="old-pwd"
                                    type={showOld ? "text" : "password"}
                                    value={oldPwd}
                                    onChange={(e) => setOldPwd(e.target.value)}
                                    autoComplete="current-password"
                                    autoFocus
                                    className="pr-10"
                                    data-testid="old-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowOld((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                    aria-label={showOld ? "Hide password" : "Show password"}
                                >
                                    {showOld ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="new-pwd" className="text-sm font-semibold">
                                New password
                            </Label>
                            <div className="relative">
                                <Input
                                    id="new-pwd"
                                    type={showNew ? "text" : "password"}
                                    value={newPwd}
                                    onChange={(e) => setNewPwd(e.target.value)}
                                    autoComplete="new-password"
                                    className="pr-10"
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNew((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                                    aria-label={showNew ? "Hide password" : "Show password"}
                                >
                                    {showNew ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                            <ul className="mt-2 space-y-1" data-testid="password-requirements">
                                {reqState.map((r) => (
                                    <li
                                        key={r.label}
                                        className={`text-xs flex items-center gap-1.5 ${
                                            r.ok
                                                ? "text-primary"
                                                : "text-muted-foreground"
                                        }`}
                                    >
                                        <Lock
                                            className={`h-3 w-3 ${
                                                r.ok ? "opacity-100" : "opacity-50"
                                            }`}
                                        />
                                        {r.label}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <Label htmlFor="confirm-pwd" className="text-sm font-semibold">
                                Confirm new password
                            </Label>
                            <Input
                                id="confirm-pwd"
                                type={showNew ? "text" : "password"}
                                value={confirmPwd}
                                onChange={(e) => setConfirmPwd(e.target.value)}
                                autoComplete="new-password"
                                data-testid="confirm-password-input"
                            />
                            {confirmPwd && !matches && (
                                <p className="text-xs text-destructive mt-1.5">
                                    Passwords don&rsquo;t match.
                                </p>
                            )}
                        </div>

                        {error && (
                            <div
                                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive flex items-start gap-2"
                                data-testid="change-password-error"
                            >
                                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={!canSubmit}
                            className="w-full bg-primary hover:bg-primary/90 gap-1.5"
                            data-testid="change-password-submit"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Updating…
                                </>
                            ) : (
                                <>
                                    <ShieldCheck className="h-4 w-4" />
                                    {isForced ? "Set password & continue" : "Update password"}
                                </>
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
